import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { useFetchContext } from "../../../utils/fetch";
import { useAuthHeaders } from "../../../utils/query/query";
import {
  INTERVALS,
  MetricsDashboard,
  TIME_RANGES,
  defaultIntervalForRange,
  fetchClusterMetrics,
  groupByMetric,
  rangeToIsoWindow,
} from "./metricsApi";
import MetricChart from "./MetricChart";

/**
 * Cluster metrics dashboard, recreating V2's metrics tab on the ported
 * GET /api/cluster/{name}/metrics endpoint: a time-range and interval selector above one card per
 * metric family (Redis CPU and memory, conductor pod CPU and memory), each charting every
 * resource that reported in the window.
 */
export default function MetricsTab({
  clusterName,
  organizationId,
}: {
  clusterName?: string;
  organizationId?: string;
}) {
  const context = useFetchContext();
  // fetchWithContext attaches no auth headers of its own; a direct caller must pass them.
  const authHeaders = useAuthHeaders();
  const [rangeHours, setRangeHours] = useState<number>(TIME_RANGES[0].hours);
  const [interval, setInterval] = useState<string>(
    defaultIntervalForRange(TIME_RANGES[0].hours)
  );

  // Recomputed per query key rather than per render, so the window does not drift on re-render.
  const window = useMemo(
    () => rangeToIsoWindow(rangeHours),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rangeHours, interval, clusterName]
  );

  const query = useQuery<MetricsDashboard>(
    ["cluster-metrics", clusterName, window.from, window.to, interval],
    () =>
      fetchClusterMetrics(
        context,
        authHeaders,
        clusterName as string,
        window.from,
        window.to,
        interval,
        organizationId
      ),
    { enabled: Boolean(clusterName), keepPreviousData: true }
  );

  const groups = useMemo(
    () => groupByMetric(query.data?.series ?? []),
    [query.data]
  );

  const onRangeChange = (hours: number) => {
    setRangeHours(hours);
    // A month at 5-minute buckets exceeds the server's lookback cap and 400s, so move the
    // interval with the range unless the user then picks something else.
    setInterval(defaultIntervalForRange(hours));
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            label="Time Range"
            value={rangeHours}
            onChange={(e) => onRangeChange(Number(e.target.value))}
            inputProps={{ "data-testid": "metrics-range" }}
          >
            {TIME_RANGES.map((r) => (
              <MenuItem key={r.label} value={r.hours}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Interval</InputLabel>
          <Select
            label="Interval"
            value={interval}
            onChange={(e) => setInterval(String(e.target.value))}
            inputProps={{ "data-testid": "metrics-interval" }}
          >
            {INTERVALS.map((i) => (
              <MenuItem key={i.value} value={i.value}>
                {i.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Typography variant="h6" fontWeight={600}>
        Cluster Metrics
      </Typography>
      {query.data && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          {query.data.from} to {query.data.to} ({query.data.interval})
        </Typography>
      )}

      {query.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Could not load metrics for this cluster.
        </Alert>
      )}

      {query.isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : groups.length === 0 ? (
        // Metrics ingestion is per cluster, so an empty response is normal rather than a fault.
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No metrics data available for this cluster in the selected window.
        </Typography>
      ) : (
        <Stack spacing={2}>
          {groups.map((group) => (
            <Card key={group.metricId} variant="outlined">
              <CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  {group.metricName}
                </Typography>
                <MetricChart group={group} />
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}
    </Box>
  );
}
